import { useEffect } from "react";
import { Printer } from "lucide-react";
import { useRouter } from "../../store/router";
import { useI18n } from "../../i18n/useI18n";
import { RingSpinner } from "../../components/ui/RingSpinner";
import { HairlineProgress } from "../../components/ui/HairlineProgress";
import { Button } from "../../components/ui/Button";
import { Lines } from "../../components/ui/Lines";
import { ShareQrAside } from "../PrintShare/ShareQrAside";
import { usePrintJob } from "./usePrintJob";
import { derivePrintPhase, printProgress, type PrintPhase } from "./printJobView";
import { trackVisitEnd } from "../../telemetry/activity";

/** 인쇄 화면 무동작 → 처음으로 (ms, 기존 규칙). 큐에 남은 작업은 에이전트가 그대로 인쇄한다 */
const IDLE_TO_RESET_MS = 120_000;
/** 직원 호출 안내 + 처음으로 버튼을 보이는 단계 */
const ALERT_PHASES: ReadonlySet<PrintPhase> = new Set(["delayed", "failed", "paperOut"]);

/**
 * STAGE · Print(agent 모드, BOOTH-39 3단계) — 서버 인쇄 큐 상태를 2초 폴링해 그린다.
 * queued "인쇄 준비 중" → claimed·printing "인쇄 중입니다"(링 25% → 50%→95% 보간) → printed 는 thanks.
 * 20초 미클레임·에이전트 오프라인·failed 는 직원 호출 안내(+QR 힌트 + 처음으로) — 폴링은 계속해 늦은 printed 도 마침으로.
 * 받기 화면의 QR 은 소형으로 이어서 보인다 (BOOTH-63) — 지연·실패 안내가 이미 "QR 로 받을 수 있어요" 라 그땐 캡션 생략.
 * window.print()·PrintSheet 없음 — 사진은 에이전트가 S3 imageUrl 로 직접 인쇄한다.
 */
export function AgentPrintView({ printId }: { printId: string }) {
  const go = useRouter((s) => s.go);
  const resetSession = useRouter((s) => s.resetSession);
  const { t } = useI18n();
  const poll = usePrintJob(printId);
  const phase = derivePrintPhase({ status: poll.status, nowMs: poll.nowMs, startedAtMs: poll.startedAtMs });
  const progress = printProgress(poll.status, poll.nowMs, poll.printingSinceMs);

  useEffect(() => {
    if (phase === "printed") go("thanks");
  }, [phase, go]);

  useEffect(() => {
    const idleReset = () => { trackVisitEnd("idle"); resetSession(); };
    let id = window.setTimeout(idleReset, IDLE_TO_RESET_MS);
    const bump = () => {
      window.clearTimeout(id);
      id = window.setTimeout(idleReset, IDLE_TO_RESET_MS);
    };
    window.addEventListener("pointerdown", bump);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("pointerdown", bump);
    };
  }, [resetSession]);

  const alert = ALERT_PHASES.has(phase);
  const title: Record<PrintPhase, string> = {
    preparing: t.print.preparing,
    printing: t.print.title,
    printed: t.print.title,
    delayed: t.print.delayed,
    failed: t.print.failed,
    paperOut: t.print.paperOut,
  };
  // 하단 트랙 라벨은 dialog 모드의 "PRINTING · 1 / 1 · 약 18초" 대신 실제 큐 상태(라틴 킥커, 번역 없음)
  const statusLabel = `${(poll.status?.status ?? "queued").toUpperCase()} · 1 / 1`;

  return (
    <div className="view view--print">
      <div className="view__body center print-view">
        <RingSpinner size={140} accent progress={progress}>
          <Printer size={48} strokeWidth={1.6} aria-hidden />
        </RingSpinner>
        <h1><Lines text={title[phase]} /></h1>
        <p>{alert ? t.print.delayedHint : t.print.sub}</p>
        <i className="divider" aria-hidden />
        <ShareQrAside caption={alert ? undefined : t.print.delayedHint} />
        {alert ? (
          <div className="view__sub">
            <Button variant="secondary" onClick={() => { trackVisitEnd("home"); resetSession(); }}>{t.printShare.home}</Button>
          </div>
        ) : null}
      </div>
      <HairlineProgress className="print-view__progress" thick value={(progress ?? 0) * 100} label={statusLabel} />
    </div>
  );
}
